import Card from "components/DesignSystem/Card/Card";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { Delete } from "components/JournalEntry/AddJournalEntryTable";
import { useFormikContext } from "formik";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useToast } from "hooks/useToast";
import { useParams } from "react-router-dom";
import {
  useUpdateOtherAttachmentsMutation,
  useDeleteOtherAttachmentsMutation,
  FinancialClosing,
} from "store/apis/financialClosing";
import { FileObject } from "types/Models/fileObject";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import classNames from "classnames";

type OtherAttachmentContentProps = {
  index: number;
  item: FileObject;
  onFileClick: () => void;
  isEditValid?: boolean;
  entityId: string;
  folderPath: string[];
};

export const OtherAttachmentContent = ({
  index,
  item,
  onFileClick,
  isEditValid,
  entityId,
  folderPath,
}: OtherAttachmentContentProps) => {
  const { downloadFile } = useDownloadFile();
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const { alertToast } = useToast();
  const [updateOtherAttachments] = useUpdateOtherAttachmentsMutation();
  const [deleteOtherAttachments, { isLoading: isAttachmentsDeleting }] =
    useDeleteOtherAttachmentsMutation();

  const { errors } = useFormikContext<{
    other_financials: FinancialClosing["other_financials"];
  }>();

  // Helper function to check if this other financial item has validation errors
  const hasOtherFinancialError = (fieldType: string) => {
    if (!errors.other_financials || !Array.isArray(errors.other_financials)) {
      return false;
    }

    const otherFinancialError = errors.other_financials[index];
    return (
      otherFinancialError &&
      typeof otherFinancialError === "object" &&
      Boolean((otherFinancialError as any)[fieldType])
    );
  };

  const isDisabled = !isEditValid;

  const onUpdate = ({
    documentType,
    documentName,
    files,
    fileUrl,
    delete_document = false,
    documentId,
  }: {
    documentType?: string;
    documentName?: string;
    files: File[] | FileType[];
    fileUrl?: string;
    delete_document?: boolean;
    documentId?: string;
  }) => {
    try {
      if (item.uuid) {
        updateOtherAttachments({
          entityId: entityId,
          financialClosingId: financialClosingId,
          otherFinancialId: item.uuid,
          payload: {
            document_type: documentType,
            document_name: documentName,
            document_file: files[0] as File,
            document_url: fileUrl,
            delete_document: delete_document,
            document_id: documentId,
          },
        }).unwrap();
      }
    } catch (err) {
      alertToast(
        {
          message: (err as BackendError).data?.error?.message,
        },
        err as Error
      );
    }
  };

  const onDeleteAttachment = async () => {
    try {
      if (isDisabled) return;
      await deleteOtherAttachments({
        entityId: entityId,
        financialClosingId: financialClosingId,
        otherFinancialId: item.uuid,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleUpdate = debounce(
    ({ value, type }: { value: string; type: string }) => {
      onUpdate({
        files: [],
        [type]: value,
      });
    }
  );

  const showNameError = hasOtherFinancialError("document_name");
  const showDocumentError = hasOtherFinancialError("document");

  const showError = showNameError || showDocumentError;

  return (
    <Card.Root
      key={index}
      className="t-rounded-lg t-py-0 sm:!t-border-0 sm:!t-px-0"
      variant="primary"
    >
      <Card.Body className="">
        <div className="t-flex t-flex-col t-gap-4 t-pb-5 -t-mt-4">
          <div className="t-text-subtext t-text-text-60 t-flex t-items-center t-justify-between">
            <div>Attachment {index + 1}</div>
            <div
              onClick={
                !isAttachmentsDeleting ? () => onDeleteAttachment() : undefined
              }
              className="t-text-text-30"
            >
              <Delete id={""} />
            </div>
          </div>
          <div>
            <RadioGroup.Root
              className="t-flex t-justify-between t-text-body-sm"
              defaultValue={item.document_type}
              onValueChange={(e) =>
                onUpdate({
                  files: [],
                  documentType: e,
                })
              }
              disabled={isDisabled}
            >
              <RadioGroup.Content>
                <div className="t-flex t-gap-3">
                  <span>
                    <RadioGroup.Item
                      id={`${item.uuid}UPLOAD`}
                      asChild
                      value="UPLOAD"
                    >
                      <span className="-t-ml-1">Upload file</span>
                    </RadioGroup.Item>
                  </span>
                  <span>
                    <RadioGroup.Item
                      id={`${item.uuid}LINK`}
                      asChild
                      value="LINK"
                    >
                      <span className="-t-ml-1">Add link</span>
                    </RadioGroup.Item>
                  </span>
                </div>
              </RadioGroup.Content>
            </RadioGroup.Root>
          </div>
          <div className="t-w-full t-flex t-gap-1.5 t-items-center t-justify-start sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
            <div
              className={classNames("t-w-1/2 sm:t-w-full sm:t-h-auto", {
                "t-h-16": !showError,
                "t-h-[92px]": showError,
              })}
            >
              <div className="t-flex t-flex-col t-gap-1">
                <div className="t-text-body-sm t-text-text-30">Name</div>
                <BareInput
                  placeholder="Attachment Name"
                  name={`other_financials[${index}].document_name`}
                  defaultValue={item?.document_name || ""}
                  onChange={(e) =>
                    handleUpdate({
                      value: e.target.value,
                      type: "documentName",
                    })
                  }
                  disabled={isDisabled}
                  id={`${item.uuid}documentName${index}`}
                  customType={showNameError ? "error" : "success"}
                />
                {showNameError ? (
                  <div className="t-text-body-sm-italic t-italic t-text-red">
                    This field is required
                  </div>
                ) : (
                  showError && <div></div>
                )}
              </div>
            </div>
            <div
              className={classNames("t-w-1/2 sm:t-w-full sm:t-h-auto", {
                "t-h-16": !showError,
                "t-h-[92px]": showError,
              })}
            >
              {item.document_type === "LINK" ? (
                <div className="t-flex t-flex-col t-gap-1">
                  <div className="t-text-body-sm t-text-text-30">Add link</div>
                  <BareInput
                    placeholder="Provide a URL to an external document"
                    name={`other_financials[${index}].document`}
                    defaultValue={item.url || ""}
                    onChange={(e) =>
                      handleUpdate({ value: e.target.value, type: "fileUrl" })
                    }
                    disabled={isDisabled}
                    customType={showDocumentError ? "error" : "success"}
                  />
                  {showDocumentError && (
                    <div className="t-text-body-sm-italic t-italic t-text-red">
                      This field is required
                    </div>
                  )}
                </div>
              ) : (
                <div className="t-flex t-flex-col t-gap-1 -t-mt-0.5">
                  <FileInput
                    allowUploadToFolder
                    withInkleDocs
                    block
                    entityId={entityId}
                    folderPath={folderPath}
                    onDrop={(files) => {
                      const uploadedFile = files[0];
                      if ("uuid" in uploadedFile) {
                        onUpdate({
                          files: [],
                          documentId: uploadedFile.uuid,
                        });
                      }
                    }}
                    onDelete={() =>
                      onUpdate({ files: [], delete_document: true })
                    }
                    type={showDocumentError ? "error" : "default"}
                    onFileClick={onFileClick}
                    onDownload={
                      item.document?.uuid && item.document?.group_id
                        ? downloadFile.bind(null, {
                            file_id: item.document.uuid,
                            group_id: item.document.group_id,
                          })
                        : undefined
                    }
                    name={`other_financials[${index}].document`}
                    file={
                      item.document
                        ? {
                            name: item.document.name,
                            file_type:
                              (item.document.file_type as
                                | "PDF"
                                | "APPLICATION_PDF"
                                | "IMAGE") ||
                              (item.document.type as
                                | "PDF"
                                | "APPLICATION_PDF"
                                | "IMAGE"),
                            is_previewable: item.document.is_previewable,
                            uuid: item.document.uuid,
                          }
                        : undefined
                    }
                    disabled={isDisabled}
                    label="Upload file"
                  />
                  {showDocumentError && (
                    <div className="t-text-body-sm-italic t-italic t-text-red">
                      This field is required
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card.Root>
  );
};
